/**
 * appendixCitationIndex — single source of truth for which references
 * actually render in the report appendix, so the in-text `[Ref N]`
 * marker and the appendix `[Ref N]` row agree.
 *
 * The bug this fixes: `walkCitationText` (in-text markers) only checked
 * `n <= report.references.length` to decide whether a token was
 * in-bounds. But the appendix (`ReferencesSection`) FILTERS the same
 * `report.references` array before rendering:
 *
 *   1. Always-dropped rows whose `source` reads "unavailable" /
 *      "failed" / "default estimate" (sentinel rows that were never a
 *      real citable source).
 *   2. For Public-Sentiment reports, references that don't influence
 *      sentiment (`isSentimentInfluencingReference === false`).
 *   3. Uncited rows (`citedInThisReport === false`) — these are dropped
 *      from the rendered list entirely.
 *
 * When the narrative cited a `[Ref N]` whose row was filtered out, the
 * appendix had no `#ref-N` anchor: the in-text pill rendered as a live
 * `[Ref N]` link that scrolled nowhere — a dead, out-of-range citation.
 *
 * This helper recomputes the SAME predicate the appendix applies and
 * returns the set of 1-based indices that survive. `walkCitationText`
 * consumes it (optional arg — back-compat) so a token whose row was
 * filtered renders as `[Ref ?]`, consistent with what the reader can
 * actually find in the appendix.
 *
 * The index is still keyed on the ORIGINAL `report.references` position
 * (1-based) — that anchor (`#ref-{origIdx+1}`) is sacrosanct (see
 * Content-Source Fixes Prompt 4). We do NOT renumber; we only mark
 * which original indices are reachable.
 */
import type { DataSource } from '../types/siteRag';
import { isSentimentInfluencingReference } from './filterSentimentReferences';

/** Sentinel `source` substrings that mark a non-citable placeholder row. */
const SENTINEL_SOURCE_FRAGMENTS = ['unavailable', 'failed', 'default estimate'];

/**
 * True when a reference row is a non-citable sentinel (the provider
 * returned no data / the fetch failed / a hard-coded default estimate
 * stood in). Mirrors the always-applied filter in
 * `ReferencesSection.tsx`.
 */
export function isSentinelReference(ref: DataSource | undefined): boolean {
  if (!ref || typeof ref.source !== 'string') return false;
  const source = ref.source.toLowerCase();
  return SENTINEL_SOURCE_FRAGMENTS.some(fragment => source.includes(fragment));
}

export interface AppendixVisibilityOptions {
  /** Report type id — drives the PSR-only sentiment-influence filter. */
  reportType?: string;
}

/**
 * Compute the set of 1-based `report.references` indices that render in
 * the appendix. A `[Ref N]` whose N is NOT in this set has no appendix
 * row to scroll to and should render degraded (`[Ref ?]`).
 *
 * Mirrors the non-editing appendix predicate in `ReferencesSection`:
 *   - drop sentinel rows,
 *   - drop PSR non-influencing rows,
 *   - drop uncited rows (`citedInThisReport === false`).
 *
 * `citedInThisReport === undefined` is treated as cited (legacy / pre-
 * Prompt-4 reports), matching the appendix's back-compat behaviour.
 *
 * Returns `null` when `references` is undefined/empty — callers treat a
 * null set as "no appendix constraint" and fall back to the plain
 * length-bounds check (so reports with no references list keep the
 * existing degraded-mode behaviour rather than nuking every marker).
 */
export function computeAppendixVisibleRefIndices(
  references: readonly DataSource[] | undefined | null,
  opts: AppendixVisibilityOptions = {},
): Set<number> | null {
  if (!references || references.length === 0) return null;
  const isPsr = opts.reportType === 'public-sentiment';
  const visible = new Set<number>();
  references.forEach((ref, idx) => {
    if (!ref) return;
    if (isSentinelReference(ref)) return;
    if (isPsr && !isSentimentInfluencingReference(ref)) return;
    if (ref.citedInThisReport === false) return;
    visible.add(idx + 1); // 1-based — matches `#ref-{origIdx+1}`.
  });
  return visible;
}
